interface PerformanceMetric {
  name: string;
  value: number;
  timestamp: number;
  category: 'navigation' | 'resource' | 'paint' | 'custom';
}

class PerformanceService {
  private metrics: PerformanceMetric[] = [];

  // Track page load performance
  trackPageLoad(): void {
    if (typeof window !== 'undefined' && window.performance) {
      const navigation = performance.getEntriesByType(
        'navigation'
      )[0] as PerformanceNavigationTiming;
      if (navigation) {
        this.addMetric(
          'page_load',
          navigation.loadEventEnd - navigation.loadEventStart,
          'navigation'
        );
        this.addMetric(
          'dom_content_loaded',
          navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
          'navigation'
        );
        this.addMetric('first_paint', this.getFirstPaint(), 'paint');
      }
    }
  }

  // Track API call performance
  trackApiCall(url: string, duration: number): void {
    this.addMetric(`api_${url}`, duration, 'resource');
  }
  // Track custom metrics
  trackCustomMetric(name: string, value: number): void {
    this.addMetric(name, value, 'custom');
  }

  // Get performance report
  getPerformanceReport(): PerformanceMetric[] {
    return [...this.metrics];
  }

  // Clear metrics
  clearMetrics(): void {
    this.metrics = [];
  }

  private addMetric(name: string, value: number, category: PerformanceMetric['category']): void {
    this.metrics.push({
      name,
      value,
      timestamp: Date.now(),
      category,
    });
  }

  private getFirstPaint(): number {
    if (typeof window !== 'undefined' && window.performance) {
      const paintEntries = performance.getEntriesByType('paint');
      const firstPaint = paintEntries.find((entry) => entry.name === 'first-paint');
      return firstPaint ? firstPaint.startTime : 0;
    }
    return 0;
  }

  // Monitor long tasks
  startLongTaskMonitoring(): void {
    if (typeof window !== 'undefined' && 'PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.duration > 50) {
            // Tasks longer than 50ms
            this.trackCustomMetric('long_task', entry.duration);
          }
        }
      });
      observer.observe({ entryTypes: ['longtask'] });
    }
  }
}

export const performanceService = new PerformanceService();
